import React, { useEffect, useRef, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import sharedStyles from './AdminShared.module.scss';
import styles from './AdminStorageBrowser.module.scss';
import {
    checkAdminStorageObjects,
    fetchAdminStorageQuarantine,
    fetchAdminStorageObject,
    fetchAdminStorageObjects,
    markAdminStorageVideoFileDeleted,
    purgeAdminStorageQuarantine,
    quarantineAdminStorageObject,
    restoreAdminStorageQuarantine,
    searchAdminStorageVideoObjects
} from '../../../services/admin.service';
import {
    AdminStorageObjectDto,
    AdminStorageObjectListDto,
    AdminStorageQuarantineDto,
    AdminStorageVideoFileMatchDto
} from '../../../types/admin.types';
import { Routes } from '../../../types/constants';
import { useUserState } from '../../../providers/user-state.provider.ts';
import { isSuperAdmin } from '../../../utils/roles';

export const extractStorageKeys = (value: string): string[] => {
    const storageKeyPattern = /(?:^|[\s,])((?:videos|images)\/[^\s,]+)/g;
    const foundKeys = new Set<string>();
    let match = storageKeyPattern.exec(value);
    while (match) {
        foundKeys.add(normalizeKey(match[1]));
        match = storageKeyPattern.exec(value);
    }

    value
        .split(/\r?\n|,/)
        .map(normalizeKey)
        .filter((part) => part.includes('/') && !part.includes(' ') && !part.startsWith('http'))
        .forEach((part) => foundKeys.add(part));

    return Array.from(foundKeys).filter(Boolean);
};

const normalizeKey = (value: string) => value.trim().replace(/^\/+/, '').replace(/[)"']+$/g, '');
type StorageObjectAction = 'quarantine' | 'mark-db-deleted';
type StorageFormatFilter = 'all' | 'Unprocessed' | 'Mp4' | 'Mp4Mobile' | 'Webm' | 'Other';
type StorageObjectStatusFilter = 'all' | 'found' | 'missing';
type StorageDbMatchFilter = 'all' | 'matched' | 'unmatched';
type StorageDbStatusFilter = 'all' | 'Ready' | 'Processing' | 'Pending' | 'Deleted' | 'Error';

interface StorageObjectGroup {
    Id: string;
    Label: string;
    Description: string;
    Objects: AdminStorageObjectDto[];
    FoundCount: number;
    MissingCount: number;
    MatchedCount: number;
    Formats: string[];
}

const buildActionKey = (action: StorageObjectAction, key: string) => `${action}:${key}`;

const storageFormatOptions: Array<{ label: string; value: StorageFormatFilter }> = [
    { label: 'All formats', value: 'all' },
    { label: 'Original source (Unprocessed)', value: 'Unprocessed' },
    { label: 'Primary playback MP4', value: 'Mp4' },
    { label: 'Mobile playback MP4', value: 'Mp4Mobile' },
    { label: 'WebM playback fallback', value: 'Webm' },
    { label: 'Other / unknown', value: 'Other' }
];

const storageDbStatusOptions: Array<{ label: string; value: StorageDbStatusFilter }> = [
    { label: 'Any DB status', value: 'all' },
    { label: 'Ready', value: 'Ready' },
    { label: 'Processing', value: 'Processing' },
    { label: 'Pending', value: 'Pending' },
    { label: 'Deleted', value: 'Deleted' },
    { label: 'Failed', value: 'Error' }
];

const quarantineStatusLabels: Record<number, string> = {
    0: 'Preparing',
    1: 'Recoverable',
    2: 'Restored',
    3: 'Permanently purged',
    4: 'Failed safely'
};

const formatDateTime = (value?: string | null) => {
    if (!value) {
        return '-';
    }

    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
};

const formatBytes = (value: number) => {
    if (value < 1024) {
        return `${value} B`;
    }

    const units = ['KB', 'MB', 'GB', 'TB'];
    let size = value / 1024;
    let unitIndex = 0;
    while (size >= 1024 && unitIndex < units.length - 1) {
        size /= 1024;
        unitIndex += 1;
    }

    return `${size.toFixed(size >= 10 ? 1 : 2)} ${units[unitIndex]}`;
};

const inferFormatFromKey = (key: string) => {
    const normalized = key.toLowerCase();
    if (normalized.includes('-mp4mobile.')) {
        return 'Mp4Mobile';
    }

    if (normalized.includes('-mp4.')) {
        return 'Mp4';
    }

    if (normalized.includes('-webm.')) {
        return 'Webm';
    }

    if (normalized.includes('-unprocessed.')) {
        return 'Unprocessed';
    }

    return 'Other';
};

const getObjectFormat = (object: AdminStorageObjectDto) => object.VideoFileMatch?.VideoFileFormat || inferFormatFromKey(object.Key);

const getFormatLabel = (format: string) => {
    switch (format) {
        case 'Unprocessed':
            return 'Original source';
        case 'Mp4':
            return 'Primary MP4';
        case 'Mp4Mobile':
            return 'Mobile MP4';
        case 'Webm':
            return 'WebM fallback';
        default:
            return format || 'Unknown';
    }
};

const getStatusLabel = (status: string) => status === 'Error' ? 'Failed' : status;

const getFileRoleLabel = (object: AdminStorageObjectDto) => (
    object.VideoFileMatch?.VideoFileRoleLabel || getFormatLabel(getObjectFormat(object))
);

const isFileActivelyProcessing = (match?: AdminStorageVideoFileMatchDto | null) => Boolean(
    match
    && (match.HasProcessingLease
        || match.VideoFileStatus === 'Pending'
        || match.VideoFileStatus === 'Processing'
        || match.VideoFileStatus === 'Downloading')
);

const getQuarantineLabel = (object: AdminStorageObjectDto) => {
    const role = object.VideoFileMatch?.VideoFileRole;
    if (role === 'OriginalSource') {
        return 'Quarantine source';
    }

    if (role === 'PrimaryPlayback' || role === 'MobilePlayback' || role === 'PlaybackFallback') {
        return 'Quarantine playback';
    }

    return 'Quarantine';
};

const getStorageActionHelpText = (object: AdminStorageObjectDto) => {
    const match = object.VideoFileMatch;
    if (!match) {
        return object.Exists ? 'No database row matches this object. It can be quarantined for 30 days before permanent purge.' : null;
    }

    if (isFileActivelyProcessing(match)) {
        return 'This file is queued or being processed. Open Video Details and use its Media section.';
    }

    if (!object.Exists && match.VideoFileStatus !== 'Deleted') {
        return 'Object missing; mark DB file Deleted to reflect storage. Repair playback from the video Media section.';
    }

    if (!object.Exists) {
        return 'DB file already Deleted. Repair playback from the video Media section.';
    }

    if (match.VideoFileRole === 'OriginalSource') {
        return match.IsCurrentPlayback
            ? 'This file belongs to current playback. Open Video Details to protect the live video.'
            : 'Quarantine keeps a recovery copy for 30 days. It never queues processing.';
    }

    return match.IsCurrentPlayback
        ? 'This file belongs to current playback. Open Video Details to protect the live video.'
        : 'Quarantine keeps a recovery copy for 30 days and marks a matching DB file Deleted. It never queues processing.';
};

const getObjectSearchText = (object: AdminStorageObjectDto) => [
    object.Key,
    object.BucketName,
    object.ContentType || '',
    getObjectFormat(object),
    getFileRoleLabel(object),
    object.VideoFileMatch?.VideoId || '',
    object.VideoFileMatch?.VideoShortId || '',
    object.VideoFileMatch?.VideoTitle || '',
    object.VideoFileMatch?.VideoStatus || '',
    object.VideoFileMatch?.PublishStatus || '',
    object.VideoFileMatch?.VideoFileFormat || '',
    object.VideoFileMatch?.VideoFileRole || '',
    object.VideoFileMatch?.VideoFileRoleLabel || '',
    object.VideoFileMatch?.VideoFileStatus || '',
    object.VideoFileMatch?.MediaGenerationId || '',
    object.VideoFileMatch?.MediaGenerationLifecycle || ''
].join(' ').toLowerCase();

const filterStorageObjects = (
    objects: AdminStorageObjectDto[],
    filterText: string,
    formatFilter: StorageFormatFilter,
    objectStatusFilter: StorageObjectStatusFilter,
    dbMatchFilter: StorageDbMatchFilter,
    dbStatusFilter: StorageDbStatusFilter
) => {
    const normalizedFilterText = filterText.trim().toLowerCase();

    return objects.filter((object) => {
        const hasDbMatch = Boolean(object.VideoFileMatch);
        if (normalizedFilterText && !getObjectSearchText(object).includes(normalizedFilterText)) {
            return false;
        }

        if (formatFilter !== 'all' && getObjectFormat(object) !== formatFilter) {
            return false;
        }

        if (objectStatusFilter === 'found' && !object.Exists) {
            return false;
        }

        if (objectStatusFilter === 'missing' && object.Exists) {
            return false;
        }

        if (dbMatchFilter === 'matched' && !hasDbMatch) {
            return false;
        }

        if (dbMatchFilter === 'unmatched' && hasDbMatch) {
            return false;
        }

        if (dbStatusFilter !== 'all' && object.VideoFileMatch?.VideoFileStatus !== dbStatusFilter) {
            return false;
        }

        return true;
    });
};

const getVideoFolderKey = (key: string) => {
    const match = /^videos\/([^/]+)\//i.exec(key);
    return match ? `videos/${match[1]}/` : '';
};

const getStorageGroupId = (object: AdminStorageObjectDto) => {
    if (object.VideoFileMatch) {
        return `video:${object.VideoFileMatch.VideoId}`;
    }

    const folder = getVideoFolderKey(object.Key);
    return folder ? `folder:${folder}` : `object:${object.Key}`;
};

const buildStorageGroups = (objects: AdminStorageObjectDto[]): StorageObjectGroup[] => {
    const groupedObjects = new Map<string, AdminStorageObjectDto[]>();
    objects.forEach((object) => {
        const groupId = getStorageGroupId(object);
        groupedObjects.set(groupId, [...(groupedObjects.get(groupId) || []), object]);
    });

    return Array.from(groupedObjects.entries()).map(([id, groupObjects]) => {
        const firstObject = groupObjects[0];
        const firstMatch = groupObjects.find((object) => object.VideoFileMatch)?.VideoFileMatch;
        const folder = firstObject ? getVideoFolderKey(firstObject.Key) : '';
        const formats = Array.from(new Set(groupObjects.map((object) => getFileRoleLabel(object)))).sort();

        return {
            Id: id,
            Label: firstMatch?.VideoTitle || folder || firstObject?.Key || 'Storage objects',
            Description: firstMatch
                ? `Primary: ${getStatusLabel(firstMatch.VideoStatus)} / Publish: ${firstMatch.PublishStatus} / ${firstMatch.VideoShortId || firstMatch.VideoId}`
                : folder || 'No database video match',
            Objects: groupObjects,
            FoundCount: groupObjects.filter((object) => object.Exists).length,
            MissingCount: groupObjects.filter((object) => !object.Exists).length,
            MatchedCount: groupObjects.filter((object) => object.VideoFileMatch).length,
            Formats: formats
        };
    });
};

const StorageObjectTable = ({
    armedActionKey,
    isLoading,
    objects,
    onDelete,
    onMarkDbDeleted,
    onSelect
}: {
    armedActionKey?: string | null;
    isLoading?: boolean;
    objects: AdminStorageObjectDto[];
    onDelete?: (object: AdminStorageObjectDto) => void;
    onMarkDbDeleted?: (object: AdminStorageObjectDto) => void;
    onSelect?: (object: AdminStorageObjectDto) => void;
}) => {
    if (objects.length === 0) {
        return <div className={sharedStyles.empty}>No storage objects found.</div>;
    }

    return (
        <div className={styles.tableWrap}>
            <table className={sharedStyles.table}>
                <thead>
                    <tr>
                        <th>Status</th>
                        <th>Key</th>
                        <th>DB Match</th>
                        <th>Video</th>
                        <th>File</th>
                        <th>Role</th>
                        <th>Size</th>
                        <th>Modified</th>
                        <th>Bucket</th>
                        <th>Content Type</th>
                        {onDelete || onMarkDbDeleted ? <th>Action</th> : null}
                    </tr>
                </thead>
                <tbody>
                    {objects.map((object) => {
                        const match = object.VideoFileMatch;
                        const isProtected = Boolean(
                            match?.IsCurrentPlayback
                            || match?.MediaGenerationLifecycle === 'Candidate'
                            || isFileActivelyProcessing(match)
                        );
                        const canDelete = object.Exists && !isProtected;
                        const canMarkDbDeleted = !object.Exists
                            && Boolean(match)
                            && !isProtected
                            && (match?.VideoFileStatus === 'Ready' || match?.VideoFileStatus === 'Error');
                        const actions: Array<{ action: StorageObjectAction; label: string }> = [
                            ...(onDelete && canDelete ? [{ action: 'quarantine' as const, label: getQuarantineLabel(object) }] : []),
                            ...(onMarkDbDeleted && canMarkDbDeleted ? [{ action: 'mark-db-deleted' as const, label: 'Mark DB deleted' }] : [])
                        ];
                        const helpText = getStorageActionHelpText(object);
                        return (
                            <tr key={`${object.BucketName}:${object.Key}`}>
                                <td>
                                    <span className={`${styles.status} ${object.Exists ? styles.found : styles.missing}`}>
                                        {object.Exists ? 'Found' : 'Missing'}
                                    </span>
                                </td>
                                <td className={styles.pathCell}>
                                    <button type="button" className={styles.selectObject} onClick={() => onSelect?.(object)}>
                                        {object.Key}
                                    </button>
                                </td>
                                <td>
                                    <span className={`${styles.status} ${match ? styles.found : styles.neutral}`}>
                                        {match ? 'Matched' : 'No DB row'}
                                    </span>
                                </td>
                                <td>
                                    {match ? (
                                        <div className={styles.dbDetails}>
                                            <strong>{match.VideoTitle}</strong>
                                            <span>Primary: {getStatusLabel(match.VideoStatus)} / Publish: {match.PublishStatus}</span>
                                            <span className={styles.mono}>{match.VideoShortId || match.VideoId}</span>
                                        </div>
                                    ) : '-'}
                                </td>
                                <td>
                                    {match ? (
                                        <div className={styles.dbDetails}>
                                            <strong>{getFormatLabel(match.VideoFileFormat)}</strong>
                                            <span className={styles.mono}>{match.VideoFileFormat}</span>
                                            <span>
                                                {match.VideoFileRole === 'OriginalSource' && object.Exists
                                                    ? `Stored (DB: ${getStatusLabel(match.VideoFileStatus)})`
                                                    : getStatusLabel(match.VideoFileStatus)}
                                            </span>
                                            <span>{formatBytes(match.VideoFileSize)}</span>
                                            {match.VideoFileProcessingPriority > 0 ? <span>Priority {match.VideoFileProcessingPriority}</span> : null}
                                            {match.MediaGenerationId ? (
                                                <>
                                                    <span>
                                                        Generation: {match.MediaGenerationLifecycle || 'Unknown'}
                                                        {match.IsCurrentPlayback ? ' (current playback)' : ''}
                                                    </span>
                                                    <span className={styles.mono}>{match.MediaGenerationId}</span>
                                                </>
                                            ) : null}
                                        </div>
                                    ) : '-'}
                                </td>
                                <td>
                                    <span className={`${styles.status} ${match ? styles.neutral : styles.missing}`}>
                                        {getFileRoleLabel(object)}
                                    </span>
                                </td>
                                <td>{object.Exists ? formatBytes(object.Size) : '-'}</td>
                                <td>{formatDateTime(object.LastModifiedUtc)}</td>
                                <td>{object.BucketName || '-'}</td>
                                <td>{object.ContentType || '-'}</td>
                                {onDelete || onMarkDbDeleted ? (
                                    <td>
                                        <div className={styles.actionButtons}>
                                            {actions.map((item) => {
                                                const actionKey = buildActionKey(item.action, object.Key);
                                                const isArmed = armedActionKey === actionKey;
                                                return (
                                                    <button
                                                        key={item.action}
                                                        type="button"
                                                        className={isArmed ? sharedStyles.danger : sharedStyles.secondary}
                                                        onClick={() => {
                                                            if (item.action === 'quarantine') {
                                                                onDelete?.(object);
                                                            } else {
                                                                onMarkDbDeleted?.(object);
                                                            }
                                                        }}
                                                        disabled={isLoading}
                                                    >
                                                        {isArmed ? 'Click again' : item.label}
                                                    </button>
                                                );
                                            })}
                                            {actions.length === 0 ? (
                                                <button type="button" className={sharedStyles.secondary} disabled>
                                                    Unavailable
                                                </button>
                                            ) : null}
                                        </div>
                                        {helpText ? <div className={styles.warning}>{helpText}</div> : null}
                                    </td>
                                ) : null}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

const StorageObjectResults = ({
    armedActionKey,
    groupRelated,
    isLoading,
    objects,
    onDelete,
    onMarkDbDeleted,
    onSelect
}: {
    armedActionKey?: string | null;
    groupRelated: boolean;
    isLoading?: boolean;
    objects: AdminStorageObjectDto[];
    onDelete?: (object: AdminStorageObjectDto) => void;
    onMarkDbDeleted?: (object: AdminStorageObjectDto) => void;
    onSelect?: (object: AdminStorageObjectDto) => void;
}) => {
    if (!groupRelated) {
        return (
            <StorageObjectTable
                armedActionKey={armedActionKey}
                isLoading={isLoading}
                objects={objects}
                onDelete={onDelete}
                onMarkDbDeleted={onMarkDbDeleted}
                onSelect={onSelect}
            />
        );
    }

    const groups = buildStorageGroups(objects);
    if (groups.length === 0) {
        return <div className={sharedStyles.empty}>No storage objects found.</div>;
    }

    return (
        <div className={styles.groupList}>
            {groups.map((group) => (
                <details key={group.Id} className={styles.group} open>
                    <summary className={styles.groupSummary}>
                        <div>
                            <strong>{group.Label}</strong>
                            <span>{group.Description}</span>
                        </div>
                        <div className={styles.groupCounts}>
                            <span>{group.Objects.length} file(s)</span>
                            <span>{group.FoundCount} found</span>
                            <span>{group.MissingCount} missing</span>
                            <span>{group.MatchedCount} matched</span>
                            <span>{group.Formats.join(', ')}</span>
                        </div>
                    </summary>
                    <StorageObjectTable
                        armedActionKey={armedActionKey}
                        isLoading={isLoading}
                        objects={group.Objects}
                        onDelete={onDelete}
                        onMarkDbDeleted={onMarkDbDeleted}
                        onSelect={onSelect}
                    />
                </details>
            ))}
        </div>
    );
};

const AdminStorageBrowser = () => {
    const { getUserInfo } = useUserState();
    const user = getUserInfo();
    const token = user?.token;
    const canView = isSuperAdmin(user?.roles);
    const [prefix, setPrefix] = useState('videos/');
    const [limit, setLimit] = useState(50);
    const [objectKey, setObjectKey] = useState('');
    const [videoId, setVideoId] = useState('');
    const [bulkInput, setBulkInput] = useState('');
    const [listPages, setListPages] = useState<AdminStorageObjectListDto[]>([]);
    const [currentPageIndex, setCurrentPageIndex] = useState(0);
    const [checkedObjects, setCheckedObjects] = useState<AdminStorageObjectDto[]>([]);
    const [selectedObject, setSelectedObject] = useState<AdminStorageObjectDto | null>(null);
    const [quarantineEntries, setQuarantineEntries] = useState<AdminStorageQuarantineDto[]>([]);
    const [includeFinalizedQuarantine, setIncludeFinalizedQuarantine] = useState(false);
    const [filterText, setFilterText] = useState('');
    const [formatFilter, setFormatFilter] = useState<StorageFormatFilter>('all');
    const [objectStatusFilter, setObjectStatusFilter] = useState<StorageObjectStatusFilter>('all');
    const [dbMatchFilter, setDbMatchFilter] = useState<StorageDbMatchFilter>('all');
    const [dbStatusFilter, setDbStatusFilter] = useState<StorageDbStatusFilter>('all');
    const [groupRelated, setGroupRelated] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [armedActionKey, setArmedActionKey] = useState<string | null>(null);
    const actionTimerRef = useRef<number | null>(null);
    const listResult = listPages[currentPageIndex] || null;

    const refreshQuarantine = async (includeFinalized = includeFinalizedQuarantine) => {
        try {
            setQuarantineEntries(await fetchAdminStorageQuarantine(includeFinalized, token));
        } catch (error) {
            setErrorMessage(error instanceof Error ? error.message : 'Unable to load recovery records.');
        }
    };

    useEffect(() => {
        if (canView) {
            void fetchAdminStorageQuarantine(includeFinalizedQuarantine, token)
                .then(setQuarantineEntries)
                .catch(() => setErrorMessage('Unable to load storage recovery records.'));
        }
    }, [canView, includeFinalizedQuarantine, token]);

    useEffect(() => () => {
        if (actionTimerRef.current !== null) {
            window.clearTimeout(actionTimerRef.current);
        }
    }, []);

    if (!canView) {
        return <Navigate to={Routes.ADMIN_ANALYTICS} replace />;
    }

    const replaceStorageObject = (updatedObject: AdminStorageObjectDto) => {
        setCheckedObjects((current) => current.map((object) => object.Key === updatedObject.Key ? updatedObject : object));
        setListPages((current) => current.map((page) => ({
            ...page,
            Objects: page.Objects.map((object) => object.Key === updatedObject.Key ? updatedObject : object)
        })));
    };

    const resetArmedAction = () => {
        if (actionTimerRef.current !== null) {
            window.clearTimeout(actionTimerRef.current);
            actionTimerRef.current = null;
        }
        setArmedActionKey(null);
    };

    const armAction = (object: AdminStorageObjectDto, action: StorageObjectAction) => {
        const actionKey = buildActionKey(action, object.Key);
        if (armedActionKey === actionKey) {
            return true;
        }

        resetArmedAction();
        setArmedActionKey(actionKey);
        actionTimerRef.current = window.setTimeout(() => {
            setArmedActionKey((current) => current === actionKey ? null : current);
            actionTimerRef.current = null;
        }, 5000);
        return false;
    };

    const browsePrefix = async (
        continuationToken?: string | null,
        requestedPrefix = prefix,
        requestedLimit = limit
    ) => {
        setIsLoading(true);
        setErrorMessage(null);
        try {
            const result = await fetchAdminStorageObjects(requestedPrefix, requestedLimit, continuationToken, token);
            if (continuationToken) {
                setListPages((current) => [...current.slice(0, currentPageIndex + 1), result]);
                setCurrentPageIndex(currentPageIndex + 1);
            } else {
                setListPages([result]);
                setCurrentPageIndex(0);
            }
        } catch (error) {
            setErrorMessage(error instanceof Error ? error.message : 'Unable to list storage objects.');
        } finally {
            setIsLoading(false);
        }
    };

    const goToPreviousPage = () => {
        setCurrentPageIndex((current) => Math.max(0, current - 1));
    };

    const goToNextPage = () => {
        if (!listResult) {
            return;
        }

        if (currentPageIndex < listPages.length - 1) {
            setCurrentPageIndex((current) => current + 1);
            return;
        }

        if (listResult.IsTruncated && listResult.NextContinuationToken) {
            void browsePrefix(listResult.NextContinuationToken, listResult.Prefix, listResult.Limit);
        }
    };

    const clearFilters = () => {
        setFilterText('');
        setFormatFilter('all');
        setObjectStatusFilter('all');
        setDbMatchFilter('all');
        setDbStatusFilter('all');
    };

    const showMobileFiles = () => {
        setFilterText('');
        setFormatFilter('Mp4Mobile');
        setObjectStatusFilter('all');
        setDbMatchFilter('all');
        setDbStatusFilter('all');
    };

    const showOriginalSources = () => {
        setFilterText('');
        setFormatFilter('Unprocessed');
        setObjectStatusFilter('all');
        setDbMatchFilter('all');
        setDbStatusFilter('all');
    };

    const showPrimaryPlaybackFiles = () => {
        setFilterText('');
        setFormatFilter('Mp4');
        setObjectStatusFilter('all');
        setDbMatchFilter('all');
        setDbStatusFilter('all');
    };

    const showMissingMobileFiles = () => {
        setFilterText('');
        setFormatFilter('Mp4Mobile');
        setObjectStatusFilter('missing');
        setDbMatchFilter('all');
        setDbStatusFilter('all');
    };

    const showProcessingMobileFiles = () => {
        setFilterText('');
        setFormatFilter('Mp4Mobile');
        setObjectStatusFilter('all');
        setDbMatchFilter('matched');
        setDbStatusFilter('Processing');
    };

    const showQueuedMobileFiles = () => {
        setFilterText('');
        setFormatFilter('Mp4Mobile');
        setObjectStatusFilter('all');
        setDbMatchFilter('matched');
        setDbStatusFilter('Pending');
    };

    const checkSingleObject = async () => {
        const key = normalizeKey(objectKey);
        if (!key) {
            setErrorMessage('Enter an object key to check.');
            return;
        }

        setIsLoading(true);
        setErrorMessage(null);
        try {
            const result = await fetchAdminStorageObject(key, token);
            setCheckedObjects([result]);
            setSelectedObject(result);
        } catch (error) {
            setErrorMessage(error instanceof Error ? error.message : 'Unable to check storage object.');
        } finally {
            setIsLoading(false);
        }
    };

    const checkBulkObjects = async () => {
        const keys = extractStorageKeys(bulkInput);
        if (keys.length === 0) {
            setErrorMessage('Paste one or more storage keys to check.');
            return;
        }

        setIsLoading(true);
        setErrorMessage(null);
        try {
            const results = await checkAdminStorageObjects({ Keys: keys }, token);
            setCheckedObjects(results);
            setSelectedObject(results[0] ?? null);
        } catch (error) {
            setErrorMessage(error instanceof Error ? error.message : 'Unable to check storage objects.');
        } finally {
            setIsLoading(false);
        }
    };

    const searchByVideoId = async () => {
        const value = videoId.trim();
        if (!value) {
            setErrorMessage('Enter a database video ID or public shortcode.');
            return;
        }

        setIsLoading(true);
        setErrorMessage(null);
        try {
            const results = await searchAdminStorageVideoObjects(value, token);
            setCheckedObjects(results);
            setSelectedObject(results[0] ?? null);
            if (results.length === 0) setErrorMessage('No database video files matched that video ID.');
        } catch (error) {
            setErrorMessage(error instanceof Error ? error.message : 'Unable to search by video ID.');
        } finally {
            setIsLoading(false);
        }
    };

    const quarantineStorageObject = async (object: AdminStorageObjectDto) => {
        if (!armAction(object, 'quarantine')) {
            return;
        }

        setIsLoading(true);
        setErrorMessage(null);
        try {
            await quarantineAdminStorageObject(object.Key, token);
            const result = await fetchAdminStorageObject(object.Key, token);
            replaceStorageObject(result);
            setSelectedObject(result);
            await refreshQuarantine();
            resetArmedAction();
        } catch (error) {
            setErrorMessage(error instanceof Error ? error.message : 'Unable to quarantine storage object. The original was retained unless the recovery copy was verified.');
        } finally {
            setIsLoading(false);
        }
    };

    const restoreQuarantine = async (entry: AdminStorageQuarantineDto) => {
        const actionKey = `restore:${entry.Id}`;
        if (armedActionKey !== actionKey) {
            resetArmedAction();
            setArmedActionKey(actionKey);
            actionTimerRef.current = window.setTimeout(resetArmedAction, 5000);
            return;
        }

        setIsLoading(true);
        setErrorMessage(null);
        try {
            await restoreAdminStorageQuarantine(entry.Id, token);
            try {
                const restoredObject = await fetchAdminStorageObject(entry.OriginalKey, token);
                replaceStorageObject(restoredObject);
                setSelectedObject((current) => current?.Key === entry.OriginalKey ? restoredObject : current);
            } catch {
                setErrorMessage('The object was restored, but the current results could not be refreshed. Check the original key again.');
            }
            await refreshQuarantine();
            resetArmedAction();
        } catch (error) {
            setErrorMessage(error instanceof Error ? error.message : 'Unable to restore the object.');
        } finally {
            setIsLoading(false);
        }
    };

    const purgeQuarantine = async (entry: AdminStorageQuarantineDto) => {
        const actionKey = `purge:${entry.Id}`;
        if (armedActionKey !== actionKey) {
            resetArmedAction();
            setArmedActionKey(actionKey);
            actionTimerRef.current = window.setTimeout(resetArmedAction, 5000);
            return;
        }

        setIsLoading(true);
        setErrorMessage(null);
        try {
            await purgeAdminStorageQuarantine(entry.Id, token);
            await refreshQuarantine();
            resetArmedAction();
        } catch (error) {
            setErrorMessage(error instanceof Error ? error.message : 'Unable to permanently purge the recovery copy.');
        } finally {
            setIsLoading(false);
        }
    };

    const markStorageVideoFileDeleted = async (object: AdminStorageObjectDto) => {
        if (!armAction(object, 'mark-db-deleted')) {
            return;
        }

        setIsLoading(true);
        setErrorMessage(null);
        try {
            const result = await markAdminStorageVideoFileDeleted(object.Key, token);
            replaceStorageObject(result);
            setSelectedObject(result);
            resetArmedAction();
        } catch (error) {
            setErrorMessage(error instanceof Error ? error.message : 'Unable to mark database file deleted.');
        } finally {
            setIsLoading(false);
        }
    };

    const filteredListObjects = listResult
        ? filterStorageObjects(listResult.Objects, filterText, formatFilter, objectStatusFilter, dbMatchFilter, dbStatusFilter)
        : [];
    const filteredCheckedObjects = filterStorageObjects(checkedObjects, filterText, formatFilter, objectStatusFilter, dbMatchFilter, dbStatusFilter);
    const checkedFoundCount = checkedObjects.filter((object) => object.Exists).length;
    const filteredCheckedFoundCount = filteredCheckedObjects.filter((object) => object.Exists).length;
    const activeFilterCount = [
        filterText.trim(),
        formatFilter !== 'all',
        objectStatusFilter !== 'all',
        dbMatchFilter !== 'all',
        dbStatusFilter !== 'all'
    ].filter(Boolean).length;
    const canPageForward = Boolean(listResult && (currentPageIndex < listPages.length - 1 || listResult.IsTruncated));

    return (
        <div className={sharedStyles.page}>
            <header className={sharedStyles.header}>
                <div>
                    <h1>Storage Browser</h1>
                    <p>Find physical objects, verify database relationships, and use recoverable cleanup.</p>
                </div>
            </header>

            <div className={sharedStyles.readOnlyBlock}>
                <strong>Use this page to inspect and clean up storage only.</strong>
                {' '}Cleanup first creates and verifies a private recovery copy retained for 30 days. Current playback and candidate replacements are protected.
                Use the video Media section for repair of missing or failed media.
            </div>

            {errorMessage ? <div className={sharedStyles.readOnlyBlock}>{errorMessage}</div> : null}

            <div className={styles.controls}>
                <section className={sharedStyles.panel}>
                    <h2>Browse Prefix</h2>
                    <div className={styles.formRow}>
                        <div className={sharedStyles.field}>
                            <label htmlFor="storage-prefix">Prefix</label>
                            <input
                                id="storage-prefix"
                                value={prefix}
                                onChange={(event) => setPrefix(event.target.value)}
                                placeholder="videos/"
                            />
                        </div>
                        <div className={`${sharedStyles.field} ${styles.limitField}`}>
                            <label htmlFor="storage-limit">Limit</label>
                            <input
                                id="storage-limit"
                                type="number"
                                min="1"
                                max="100"
                                value={limit}
                                onChange={(event) => setLimit(Number(event.target.value))}
                            />
                        </div>
                        <button type="button" className={sharedStyles.primary} onClick={() => browsePrefix(null)} disabled={isLoading}>
                            Browse
                        </button>
                    </div>
                </section>

                <section className={sharedStyles.panel}>
                    <h2>Find Exact Object</h2>
                    <div className={sharedStyles.field}>
                        <label htmlFor="storage-object-key">Exact key</label>
                        <input
                            id="storage-object-key"
                            value={objectKey}
                            onChange={(event) => setObjectKey(event.target.value)}
                            placeholder="videos/id/id-Mp4.mp4 or videos/id/id-Mp4Mobile.mp4"
                        />
                    </div>
                    <div className={sharedStyles.actions}>
                        <button type="button" className={sharedStyles.secondary} onClick={checkSingleObject} disabled={isLoading}>
                            Check Key
                        </button>
                    </div>
                </section>

                <section className={sharedStyles.panel}>
                    <h2>Find by Video ID</h2>
                    <div className={sharedStyles.field}>
                        <label htmlFor="storage-video-id">Database ID or public shortcode</label>
                        <input
                            id="storage-video-id"
                            value={videoId}
                            onChange={(event) => setVideoId(event.target.value)}
                            placeholder="Video GUID or shortcode"
                        />
                    </div>
                    <button type="button" className={sharedStyles.secondary} onClick={searchByVideoId} disabled={isLoading}>
                        Find related files
                    </button>
                </section>
            </div>

            {selectedObject ? (
                <section className={`${sharedStyles.panel} ${styles.inspector}`} aria-labelledby="selected-storage-object-heading">
                    <div className={sharedStyles.header}>
                        <div>
                            <h2 id="selected-storage-object-heading">Selected object</h2>
                            <p className={styles.mono}>{selectedObject.Key}</p>
                        </div>
                        <span className={`${styles.status} ${selectedObject.Exists ? styles.found : styles.missing}`}>
                            {selectedObject.Exists ? 'Found in storage' : 'Missing from storage'}
                        </span>
                    </div>
                    <dl className={styles.inspectorGrid}>
                        <div><dt>Bucket</dt><dd>{selectedObject.BucketName || '-'}</dd></div>
                        <div><dt>Physical size</dt><dd>{selectedObject.Exists ? formatBytes(selectedObject.Size) : '-'}</dd></div>
                        <div><dt>Modified</dt><dd>{formatDateTime(selectedObject.LastModifiedUtc)}</dd></div>
                        <div><dt>Database relation</dt><dd>{selectedObject.VideoFileMatch ? 'Matched VideoFile' : 'No database row'}</dd></div>
                        <div><dt>Generation</dt><dd>{selectedObject.VideoFileMatch?.MediaGenerationLifecycle || '-'}</dd></div>
                        <div><dt>Playback</dt><dd>{selectedObject.VideoFileMatch?.IsCurrentPlayback ? 'Current playback — protected' : 'Not current playback'}</dd></div>
                        <div><dt>Database status</dt><dd>{selectedObject.VideoFileMatch?.VideoFileStatus || '-'}</dd></div>
                        <div><dt>Database size</dt><dd>{selectedObject.VideoFileMatch ? formatBytes(selectedObject.VideoFileMatch.VideoFileSize) : '-'}</dd></div>
                    </dl>
                    {selectedObject.VideoFileMatch ? (
                        <div className={styles.inspectorActions}>
                            <div><strong>{selectedObject.VideoFileMatch.VideoTitle}</strong><span>{selectedObject.VideoFileMatch.VideoShortId}</span></div>
                            <Link className={sharedStyles.primary} to={`${Routes.ADMIN_VIDEOS}/${selectedObject.VideoFileMatch.VideoId}`}>Open Video Details</Link>
                        </div>
                    ) : <div className={styles.unlinkedNotice}>This is a physical storage object with no matching VideoFile database row.</div>}
                </section>
            ) : null}

            <section className={sharedStyles.panel}>
                <h2>Bulk Upload Check</h2>
                <div className={sharedStyles.field}>
                    <label htmlFor="storage-bulk-keys">Keys or CSV rows</label>
                    <textarea
                        id="storage-bulk-keys"
                        className={styles.textarea}
                        value={bulkInput}
                        onChange={(event) => setBulkInput(event.target.value)}
                        placeholder="Paste DB rows or object keys containing videos/... paths"
                    />
                </div>
                <div className={sharedStyles.actions}>
                    <button type="button" className={sharedStyles.primary} onClick={checkBulkObjects} disabled={isLoading}>
                        Check Pasted Paths
                    </button>
                </div>
            </section>

            <section className={sharedStyles.panel}>
                <div className={sharedStyles.header}>
                    <div>
                        <h2>Result Filters</h2>
                        <div className={styles.summary}>
                            {activeFilterCount === 0 ? 'No filters applied.' : `${activeFilterCount} filter(s) applied.`}
                        </div>
                    </div>
                    <button type="button" className={sharedStyles.secondary} onClick={clearFilters}>
                        Clear Filters
                    </button>
                </div>
                <div className={styles.filterGrid}>
                    <div className={sharedStyles.field}>
                        <label htmlFor="storage-filter-text">Search within results</label>
                        <input
                            id="storage-filter-text"
                            value={filterText}
                            onChange={(event) => setFilterText(event.target.value)}
                            placeholder="Title, id, key, bucket, status..."
                        />
                    </div>
                    <div className={sharedStyles.field}>
                        <label htmlFor="storage-format-filter">Format</label>
                        <select
                            id="storage-format-filter"
                            value={formatFilter}
                            onChange={(event) => setFormatFilter(event.target.value as StorageFormatFilter)}
                        >
                            {storageFormatOptions.map((option) => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                        </select>
                    </div>
                    <div className={sharedStyles.field}>
                        <label htmlFor="storage-object-status-filter">Object status</label>
                        <select
                            id="storage-object-status-filter"
                            value={objectStatusFilter}
                            onChange={(event) => setObjectStatusFilter(event.target.value as StorageObjectStatusFilter)}
                        >
                            <option value="all">Found or missing</option>
                            <option value="found">Found in storage</option>
                            <option value="missing">Missing from storage</option>
                        </select>
                    </div>
                    <div className={sharedStyles.field}>
                        <label htmlFor="storage-db-match-filter">DB match</label>
                        <select
                            id="storage-db-match-filter"
                            value={dbMatchFilter}
                            onChange={(event) => setDbMatchFilter(event.target.value as StorageDbMatchFilter)}
                        >
                            <option value="all">Matched or unmatched</option>
                            <option value="matched">Has DB video file</option>
                            <option value="unmatched">No DB video file</option>
                        </select>
                    </div>
                    <div className={sharedStyles.field}>
                        <label htmlFor="storage-db-status-filter">DB file status</label>
                        <select
                            id="storage-db-status-filter"
                            value={dbStatusFilter}
                            onChange={(event) => setDbStatusFilter(event.target.value as StorageDbStatusFilter)}
                        >
                            {storageDbStatusOptions.map((option) => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                        </select>
                    </div>
                    <label className={`${sharedStyles.inlineCheckbox} ${styles.groupToggle}`}>
                        <input
                            type="checkbox"
                            checked={groupRelated}
                            onChange={(event) => setGroupRelated(event.target.checked)}
                        />
                        Group related video files
                    </label>
                </div>
                <div className={styles.quickFilters}>
                    <button type="button" className={sharedStyles.secondary} onClick={showOriginalSources}>
                        Original sources
                    </button>
                    <button type="button" className={sharedStyles.secondary} onClick={showPrimaryPlaybackFiles}>
                        Primary MP4 playback
                    </button>
                    <button type="button" className={sharedStyles.secondary} onClick={showMobileFiles}>
                        Mobile MP4 playback
                    </button>
                    <button type="button" className={sharedStyles.secondary} onClick={showMissingMobileFiles}>
                        Missing mobile
                    </button>
                    <button type="button" className={sharedStyles.secondary} onClick={showProcessingMobileFiles}>
                        Processing mobile
                    </button>
                    <button type="button" className={sharedStyles.secondary} onClick={showQueuedMobileFiles}>
                        Queued mobile
                    </button>
                </div>
            </section>

            {listResult ? (
                <section className={sharedStyles.panel}>
                    <div className={sharedStyles.header}>
                        <div>
                            <h2>Prefix Results</h2>
                            <div className={styles.summary}>
                                {filteredListObjects.length} of {listResult.Objects.length} object(s) for {listResult.Prefix}.
                                {' '}Page {currentPageIndex + 1} of {listPages.length}{listResult.IsTruncated ? '+' : ''}.
                            </div>
                        </div>
                        <div className={styles.pager}>
                            <button
                                type="button"
                                className={sharedStyles.secondary}
                                onClick={goToPreviousPage}
                                disabled={isLoading || currentPageIndex === 0}
                            >
                                Previous
                            </button>
                            <button
                                type="button"
                                className={sharedStyles.secondary}
                                onClick={goToNextPage}
                                disabled={isLoading || !canPageForward}
                            >
                                Next Page
                            </button>
                        </div>
                    </div>
                    <StorageObjectResults
                        armedActionKey={armedActionKey}
                        groupRelated={groupRelated}
                        isLoading={isLoading}
                        objects={filteredListObjects}
                        onDelete={quarantineStorageObject}
                        onMarkDbDeleted={markStorageVideoFileDeleted}
                        onSelect={setSelectedObject}
                    />
                </section>
            ) : null}

            {checkedObjects.length > 0 ? (
                <section className={sharedStyles.panel}>
                    <h2>Upload Check Results</h2>
                    <div className={styles.summary}>
                        {filteredCheckedObjects.length} of {checkedObjects.length} result(s) shown.
                        {' '}{filteredCheckedFoundCount} found, {filteredCheckedObjects.length - filteredCheckedFoundCount} missing.
                        {' '}Original check: {checkedFoundCount} found, {checkedObjects.length - checkedFoundCount} missing.
                    </div>
                    <StorageObjectResults
                        armedActionKey={armedActionKey}
                        groupRelated={groupRelated}
                        isLoading={isLoading}
                        objects={filteredCheckedObjects}
                        onDelete={quarantineStorageObject}
                        onMarkDbDeleted={markStorageVideoFileDeleted}
                        onSelect={setSelectedObject}
                    />
                </section>
            ) : null}

            <section className={sharedStyles.panel} aria-labelledby="storage-recovery-heading">
                <div className={sharedStyles.header}>
                    <div>
                        <h2 id="storage-recovery-heading">Recovery bin</h2>
                        <div className={styles.summary}>Objects remain restorable for 30 days. Permanent purge is available only after that date.</div>
                    </div>
                    <label className={sharedStyles.inlineCheckbox}>
                        <input type="checkbox" checked={includeFinalizedQuarantine} onChange={(event) => setIncludeFinalizedQuarantine(event.target.checked)} />
                        Include purged records
                    </label>
                </div>
                {quarantineEntries.length === 0 ? <div className={sharedStyles.empty}>No recovery records.</div> : (
                    <div className={styles.recoveryList}>
                        {quarantineEntries.map((entry) => {
                            const restoreKey = `restore:${entry.Id}`;
                            const purgeKey = `purge:${entry.Id}`;
                            return (
                                <article key={entry.Id} className={styles.recoveryCard}>
                                    <div>
                                        <div className={styles.recoveryTitle}>
                                            <strong>{entry.VideoTitle || 'Unlinked storage object'}</strong>
                                            <span className={`${styles.status} ${entry.Status === 1 ? styles.found : styles.neutral}`}>{quarantineStatusLabels[entry.Status]}</span>
                                        </div>
                                        <div className={styles.mono}>{entry.OriginalKey}</div>
                                        <div className={styles.summary}>Moved {formatDateTime(entry.QuarantinedAtUtc)} by {entry.ActorUserName}. Retained until {formatDateTime(entry.PurgeAfterUtc)}.</div>
                                        {entry.FailureMessage ? <div className={styles.warning}>{entry.FailureMessage}</div> : null}
                                    </div>
                                    <div className={styles.actionButtons}>
                                        {entry.CanRestore ? <button type="button" className={armedActionKey === restoreKey ? sharedStyles.primary : sharedStyles.secondary} disabled={isLoading} onClick={() => restoreQuarantine(entry)}>{armedActionKey === restoreKey ? 'Confirm restore' : 'Restore'}</button> : null}
                                        {entry.CanPurge ? <button type="button" className={armedActionKey === purgeKey ? sharedStyles.danger : sharedStyles.secondary} disabled={isLoading} onClick={() => purgeQuarantine(entry)}>{armedActionKey === purgeKey ? 'Permanently purge' : 'Purge recovery copy'}</button> : null}
                                    </div>
                                </article>
                            );
                        })}
                    </div>
                )}
            </section>
        </div>
    );
};

export default AdminStorageBrowser;
